import React, { useEffect, useState, useMemo } from "react";
import { Header } from "../../components/header/Header";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { getAllContestThunk as onGetAllContestThunk } from "../../slices/contest/thunk";
import { useNavigate, useParams } from "react-router-dom";
import { getRole } from "../../utils/GetRole";
import { getAuthUser } from "../../utils/GetAuthUser";

const defaultImage = "/default.png";

export const MyContest = () => {
  
  const fileUrl = process.env.REACT_APP_FILE_URL;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { statusCode } = useParams();

  const [role] = useState(getRole());
  const [contestData, setContestData] = useState([]);

  // ✅ Memoize the auth user so it doesn't re-trigger useEffect
  const authUser = useMemo(() => getAuthUser(), []);

  // ✅ Redux selector (memoized)
  const contestSelector = createSelector(
    (state) => state.contest,
    (contest) => ({
      isLoading: contest.isLoading,
      contestList: contest.contestList,
    })
  );

  const { isLoading, contestList } = useSelector(contestSelector);

  // ✅ Fetch data once when component mounts
  useEffect(() => {
    if (!authUser?.user?.id) return; // prevent dispatch if not logged in

    const payload = {
      statusCode: statusCode || null,
      user_id: authUser.user.id,
      showRefferalContest : authUser.user.referral_code != null ? 1 :2
    };

    dispatch(onGetAllContestThunk(payload));
  }, [dispatch, statusCode, authUser?.user?.id]); // these won't change normally

  // ✅ Update state when redux data changes
  useEffect(() => {
    // console.log("Contest List Updated:", contestList); // Debug log
    if (contestList?.length) {
      setContestData(contestList);
    } else {
      setContestData([]);
    }
  }, [contestList]);

  // ✅ Navigation handlers
  const handleNavigateToSketchDetail = (contestId) => {
    navigate(`/${role}/contest-detail/2/${contestId}`);
  };

  const handleNavigateToCreateContest = (contestId) => {
    var isRefference = false;

    try{
      var usedata = JSON.parse(localStorage.getItem("authUser")).user;
      if(usedata?.referral_code != null){
        isRefference = true;
      }
    }catch(e){}

    if(isRefference){
      navigate(`/${role}/contest-artist-event-request/${contestId}`);

    }else{

      navigate(`/${role}/contest-artist-request/${contestId}`);
    }
  };

  const SkeletonCard = () => (
    <div className="w-full flex flex-col justify-between relative h-full p-3 border border-border_color rounded-xl animate-pulse bg-white">
      <div className="w-full h-[175px] rounded-xl overflow-hidden bg-gray-200" />

      <span className="absolute top-4 right-4 w-20 h-6 bg-gray-200 rounded-full" />

      <div className="flex flex-col justify-between flex-1 mt-3">
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="w-[130px] h-8 rounded-3xl bg-gray-200" />
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 w-full mt-4">
          <div className="h-10 bg-gray-200 rounded-[10px] w-full" />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Header */}
      <header className="fixed top-0 left-0 md:left-[205px] md:w-[calc(100%-205px)] z-40 bg-white w-full h-14 md:h-[84px] flex items-center justify-center px-4 sm:px-[51px] border-b border-border_color">
        <Header />
      </header>

      <div className="w-full mx-auto bg-[#F7F7F7] min-h-screen mt-2 ">
        {/* Page Title */}
        <div className="flex items-center justify-start mb-6">
          <h1 className="font-bold text-black text-[22px] font-grotesk">
            My Contests
          </h1>
        </div>

        {/* Loading / Empty / Grid */}
        {isLoading ? (
          <div className="grid gap-3 sm:gap-4 grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {Array.from({ length: 10 }).map((_, idx) => (
              <SkeletonCard key={idx} />
            ))}
          </div>
        ) : contestData.length > 0 ? (
          <div className="grid gap-3 sm:gap-4 grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {contestData.map((card) => {
              const contestImage = card.contestImage
                ? `${fileUrl}/${card.contestImage}`
                : defaultImage;

              return (
                <div
                  key={card.id}
                  className="flex flex-col h-full justify-between bg-transparent overflow-hidden p-3 border border-border_color rounded-xl relative"
                >
                  {/* Image */}
                  <div className="w-full h-[175px] rounded-2xl overflow-hidden">
                    
                    <img
                      src={contestImage}
                      alt={card.title}
                      className="object-cover object-center w-full h-full block"
                    />
                  </div>
                  <span className="text-sm text-black font-medium font-grotesk absolute top-4 right-4 bg-white px-2 py-1 rounded-full shadow-md">
                    {card.status}
                  </span>
                  {/* Text Section */}
                  <div className="py-3 flex flex-col flex-grow justify-between">
                    {/* Category */}
                    <div className="flex items-center justify-center border border-[#DDDDDD] py-[6px] px-[14px] w-[130px] rounded-3xl ">
                      <span className="font-bold text-[11px] text-[#262626] font-grotesk text-center">
                        {card.title || card.contestCategory}
                      </span>
                    </div>

                    {/* Description */}
                    <div className="mt-3 text-start">
                      <p className="text-[#696565] text-sm font-grotesk font-medium line-clamp-2">
                        {card.description || card.contestDescription}
                      </p>
                    </div>

                    {/* User Info */}
                    {/* <div className="flex items-center justify-center gap-2 mt-3">
                      <span className="text-sm font-medium text-[#262626]">
                        {card.userName}
                      </span>
                    </div> */}

                    {/* Buttons */}
                    <div className="flex items-center justify-center gap-2 w-full mt-4 flex-wrap">
                      <button
                        className="w-full flex flex-1 items-center justify-center text-xs font-bold border border-border_color font-grotesk text-primary py-3 px-5 rounded-[10px] whitespace-nowrap tracking-wide hover:text-white hover:bg-primary transition-all delay-75"
                        onClick={() => handleNavigateToSketchDetail(card.id)}
                      >
                        View Details
                      </button>

                      {card.is_publish == 2 && (
                        <button
                          className="w-full flex flex-1 items-center justify-center text-xs font-bold text-white bg-primary font-grotesk  py-3 px-2 tracking-wide rounded-[10px] whitespace-nowrap hover:bg-primary hover:text-white transition-all"
                          onClick={() => handleNavigateToCreateContest(card.id)}
                        >
                          Hire Artist
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-gray-500">No contests found</p>
        )}
      </div>
    </>
  );
};
